// ============================================================================
// Beacon — calculation engine
// Pure, dependency-free, deterministic. This is the reusable "brain":
// the same module runs in the browser (instant feedback) and on the server
// (report generation + audit). No React, no DOM, no side effects.
// ============================================================================

import type { Deal, Gates, Metrics, Score, TakeoutScenario } from "../types";

/** Annual mortgage constant (payment as a fraction of principal). */
export function mortgageConstant(rate: number, years: number): number {
  const r = rate / 12;
  const n = years * 12;
  if (r === 0) return 1 / years;
  return (r / (1 - Math.pow(1 + r, -n))) * 12;
}

/** Remaining balance on an amortizing loan after `afterYears`. */
export function loanBalance(
  principal: number,
  rate: number,
  years: number,
  afterYears: number
): number {
  const r = rate / 12;
  const n = years * 12;
  const k = afterYears * 12;
  if (r === 0) return principal * (1 - afterYears / years);
  const pay = (principal * r) / (1 - Math.pow(1 + r, -n));
  const bal =
    principal * Math.pow(1 + r, k) - (pay * (Math.pow(1 + r, k) - 1)) / r;
  return Math.max(0, bal);
}

/** Internal rate of return via bisection. cfs[0] should be negative. */
export function irr(cfs: number[]): number | null {
  let lo = -0.9;
  let hi = 1.0;
  const npv = (r: number) =>
    cfs.reduce((s, c, i) => s + c / Math.pow(1 + r, i), 0);
  if (npv(lo) * npv(hi) > 0) return null;
  for (let i = 0; i < 100; i++) {
    const mid = (lo + hi) / 2;
    if (npv(mid) > 0) lo = mid;
    else hi = mid;
  }
  return (lo + hi) / 2;
}

const clamp = (v: number, a: number, b: number) => Math.max(a, Math.min(b, v));

// ---------------------------------------------------------------------------
// Main entry point
// ---------------------------------------------------------------------------
export function calc(d: Deal): Metrics {
  const m = {} as Metrics;

  // income cascade
  m.gpr = (d.u2c * d.u2r + d.u1c * d.u1r) * 12;
  m.vac = m.gpr * d.vacPct;
  m.egi = m.gpr - m.vac + d.otherInc;
  m.mgmt = m.egi * d.mgmtPct;
  m.opex =
    d.taxes + d.insBase + m.mgmt + d.repairs + d.utilities + d.admin + d.reserves;
  m.noi = m.egi - m.opex;

  // cost & value
  m.allIn = d.purchase + d.rehab + d.soft;
  m.stabVal = m.noi / d.exitCap;
  m.capOnCost = m.noi / m.allIn;
  m.capOnVal = m.noi / m.stabVal;
  m.grm = d.purchase / m.gpr;
  m.expRatio = m.opex / m.egi;

  // permanent loan sizing — the lesser of the LTV and DSCR tests
  m.constant = mortgageConstant(d.permRate, d.amort);
  m.permByLTV = d.targetLTV * m.stabVal;
  m.permByDSCR = m.noi / d.dscrMin / m.constant;
  m.permLoan = Math.min(m.permByLTV, m.permByDSCR);
  m.ltvBinds = m.permByLTV < m.permByDSCR;
  m.debtSvc = m.permLoan * m.constant;
  m.dscr = m.noi / m.debtSvc;
  m.debtYield = m.noi / m.permLoan;
  m.ltv = m.permLoan / m.stabVal;

  // bridge & equity
  m.bridge = d.bridgeLTC * (d.purchase + d.rehab);
  m.equity = m.allIn - m.bridge;
  m.lpEquity = d.lp1 + d.lp2 + d.lp3;
  m.gpEquity = m.equity - m.lpEquity;
  m.cashReturned = Math.max(0, m.permLoan - m.bridge);
  m.remEquity = Math.max(1, m.equity - m.cashReturned);
  m.cashFlow = m.noi - m.debtSvc;
  m.coc = m.cashFlow / m.remEquity;
  m.breakEven = (m.opex + m.debtSvc) / m.gpr;

  // 5-year projection -> project IRR & equity multiple
  const hold = 5;
  const cfs: number[] = [-m.equity];
  let distTotal = 0;
  for (let y = 1; y <= hold; y++) {
    const inc =
      m.gpr * Math.pow(1 + d.rentGrow, y - 1) * (1 - d.vacPct) + d.otherInc;
    const ex =
      (m.opex - m.mgmt) * Math.pow(1 + d.expGrow, y - 1) + inc * d.mgmtPct;
    const noiY = inc - ex;
    let cf = noiY - m.debtSvc;
    if (y === 1) cf += m.cashReturned; // refi returns capital in yr 1
    if (y === hold) {
      const saleInc =
        m.gpr * Math.pow(1 + d.rentGrow, y) * (1 - d.vacPct) + d.otherInc;
      const saleEx =
        (m.opex - m.mgmt) * Math.pow(1 + d.expGrow, y) + saleInc * d.mgmtPct;
      const saleVal = (saleInc - saleEx) / d.exitCap;
      const bal = loanBalance(m.permLoan, d.permRate, d.amort, hold - 1);
      cf += saleVal * 0.95 - bal; // 5% selling cost, repay remaining loan
    }
    cfs.push(cf);
    distTotal += cf;
  }
  m.projIRR = irr(cfs);
  m.equityMult = (distTotal + m.equity) / m.equity;

  // LP IRR — proportional proxy with a promote haircut (full waterfall in v3)
  const lpShare = m.lpEquity / m.equity;
  const lpcfs: number[] = [-m.lpEquity];
  for (let y = 1; y < cfs.length; y++) {
    lpcfs.push(Math.max(0, cfs[y]) * lpShare * (1 - d.gpPromote * 0.3));
  }
  m.lpIRR = irr(lpcfs);

  // insurance stress
  m.noiStress = m.noi - (d.insStress - d.insBase);
  m.dscrStress = m.noiStress / m.debtSvc;

  // takeout stress + gates + score
  m.takeout = takeoutScenarios(d, m);
  m.gates = gates(d, m);
  m.score = score(d, m);
  return m;
}

// ---------------------------------------------------------------------------
export function takeoutScenarios(d: Deal, m: Metrics): TakeoutScenario[] {
  const scen = (exitCap: number, rate: number, ins: number) => {
    const noi = m.noi - (ins - d.insBase);
    const val = noi / exitCap;
    const k = mortgageConstant(rate, d.amort);
    const byLTV = d.targetLTV * val;
    const byDSCR = noi / d.dscrMin / k;
    const loan = Math.min(byLTV, byDSCR);
    return { loan, gap: loan - m.bridge };
  };
  return [
    { name: "Base case", note: "As underwritten", ...scen(d.exitCap, d.permRate, d.insBase) },
    { name: "Higher exit cap", note: "Exit cap +0.5%", ...scen(d.exitCap + 0.005, d.permRate, d.insBase) },
    { name: "Higher rate", note: "Rate +0.75%", ...scen(d.exitCap, d.permRate + 0.0075, d.insBase) },
    { name: "Insurance spike", note: "Ins→stress + rate +0.75%", ...scen(d.exitCap, d.permRate + 0.0075, d.insStress) },
  ];
}

// ---------------------------------------------------------------------------
export function gates(d: Deal, m: Metrics): Gates {
  // A — physical / NSPIRE
  const physIssues = d.roofAge > 15 || d.hasDeficiencies;
  let A;
  if (physIssues && d.rehab > 0)
    A = { s: "cond" as const, t: "CONDITIONAL", txt: `Won't pass today (roof ${d.roofAge} yrs / listed items) — fixable in the ${money(d.rehab)} rehab. Projected: PASS after work.` };
  else if (physIssues && d.rehab <= 0)
    A = { s: "fail" as const, t: "FAIL", txt: "Physical issues with no rehab budget — cannot pass inspection as-is." };
  else A = { s: "pass" as const, t: "PASS", txt: "No blocking physical issues identified." };

  // B — rent ceiling & reasonableness
  const ps2 = d.safmr2 * d.paymentStd;
  const pctOfSafmr = d.u2r / d.safmr2;
  let B;
  if (d.u2r <= ps2 * 1.001 && pctOfSafmr <= 1.1)
    B = { s: "pass" as const, t: "PASS", txt: `2BR target ${money(d.u2r)} is ${pctInt(pctOfSafmr)} of SAFMR — inside the payment-standard band; supported by comps.` };
  else
    B = { s: "cond" as const, t: "CONDITIONAL", txt: `Rent ${money(d.u2r)} is ${pctInt(pctOfSafmr)} of SAFMR — needs an exception payment standard (HUD approval), which is execution risk.` };

  // C — program / PHA
  const C =
    d.phaActive && d.samClear
      ? { s: "pass" as const, t: "PASS", txt: "PHA actively issuing vouchers; owner clears SAM.gov exclusion checks; PBV option noted." }
      : { s: "fail" as const, t: "FAIL", txt: "PHA inactive or owner/entity flagged — program feasibility not met." };

  // D — legal / zone / environmental / title
  const dIssues: string[] = [];
  if (d.flood !== "X") dIssues.push(`flood zone ${d.flood} (insurance cost)`);
  if (d.oz) dIssues.push("OZ benefit time-limited (2027 re-map)");
  if (d.lisPendens) dIssues.push("active pre-foreclosure (lis pendens) — motivated seller, but title must clear at closing");
  if (d.openLiens > 0) dIssues.push(`${money(d.openLiens)} in open liens/judgments — pay off from proceeds & confirm clear title`);
  let D;
  if (!d.zoning)
    D = { s: "fail" as const, t: "FAIL", txt: "Zoning nonconforming — units may be illegal. Hard stop until resolved." };
  else if (dIssues.length)
    D = { s: "cond" as const, t: "CONDITIONAL", txt: "Conditional: " + dIssues.join("; ") + ". Costs routed into the pro forma." };
  else D = { s: "pass" as const, t: "PASS", txt: "Zoning conforms; low environmental/flood risk; title clean." };

  const anyFail = [A, B, C, D].some((g) => g.s === "fail");
  return { A, B, C, D, anyFail };
}

// ---------------------------------------------------------------------------
export function score(d: Deal, m: Metrics): Score {
  const sc = (v: number, target: number) => clamp((v / target) * 78, 15, 100);

  const fin =
    (sc(m.dscr, d.dscrMin) +
      sc(m.capOnCost, 0.065) +
      sc(m.coc, 0.06) +
      sc(m.debtYield, 0.08) +
      sc(m.projIRR ?? 0.1, 0.14)) /
    5;

  let loc =
    55 +
    clamp(d.popGrow * 180, 0, 22) +
    (d.renterRising ? 6 : 0) +
    clamp((0.06 - d.unemp) * 300, -6, 10) +
    (d.oz ? 4 : 0) -
    (d.flood !== "X" ? 8 : 0);
  loc = clamp(loc, 10, 98);

  const worst = m.takeout[3];
  const near = m.takeout[2];
  let ce =
    88 -
    clamp((d.rehab / m.allIn) * 70, 0, 22) -
    (worst.gap < 0 ? 14 : near.gap < 8000 ? 6 : 0) -
    (d.flood !== "X" ? 5 : 0);
  ce = clamp(ce, 10, 98);

  const compsBase: Record<number, number> = { 1: 60, 2: 75, 3: 88 };
  let dq = (compsBase[d.comps] ?? 70) + (d.phaActive ? 6 : 0) - (d.concHigh ? 8 : 0);
  dq = clamp(dq, 10, 98);

  const composite = fin * 0.4 + loc * 0.25 + ce * 0.2 + dq * 0.15;

  let band: Score["band"];
  let cls: Score["cls"];
  if (m.gates.anyFail) {
    band = "DO NOT PROCEED";
    cls = "fail";
  } else if (composite >= 85) {
    band = "STRONG BUY";
    cls = "pass";
  } else if (composite >= 70) {
    band = "BUY";
    cls = "pass";
  } else if (composite >= 55) {
    band = "WATCH";
    cls = "watch";
  } else {
    band = "PASS";
    cls = "fail";
  }

  return {
    fin: Math.round(fin),
    loc: Math.round(loc),
    ce: Math.round(ce),
    dq: Math.round(dq),
    composite: Math.round(composite),
    band,
    cls,
    gated: m.gates.anyFail,
  };
}

// tiny local formatters (kept here so the engine stays self-contained)
function money(n: number): string {
  return "$" + Math.round(n).toLocaleString("en-US");
}
function pctInt(n: number): string {
  return Math.round(n * 100) + "%";
}
